// lib
import { Fragment, useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { encodeText, pluralize } from '@aytm/app/stats/lib/util/helpers'

// components
import { Dropdown } from 'primereact/dropdown'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

// styles
import style from '@aytm/app/stats/page/sidebar/view-options/styles/ViewOptionsPanel.module.scss'

// the option labels and the progress message are drawn as markup, so the values set into them are escaped
const plain = value => encodeText(value, false)

const Translations = ({ root, model, data, features, onTranslationChange }) => {
  const { totals, translation } = model
  const { country, locale, targetingLocales } = data

  const { totalVerbatims, totalVerbatimsByLocale, translationsMissing } = totals

  const [translationMessage, setTranslationMessage] = useState(null)
  const [translationMessageType, setTranslationMessageType] = useState(null)

  const [showTransationPopup, setShowTransationPopup] = useState(true)

  const translationsOptions = useMemo(() => {
    if (!targetingLocales) return []

    const languageNames = new Intl.DisplayNames(['en'], { type: 'language' })

    const researcherLanguage = locale.split('_', 1)[0].toLowerCase()
    const surveyLanguages = targetingLocales.map(item => item.split('_', 1)[0].toLowerCase())

    const showOriginalLangsLabel =
      'WRD' != country.id && (1 < surveyLanguages.length || !surveyLanguages.some(item => 'en' == item))
    const originalLabel = `Original ${
      showOriginalLangsLabel ? `(${plain(surveyLanguages.join(', ').toUpperCase())})` : 'verbatims'
    }`
    const originalText = `Show original ${
      showOriginalLangsLabel ? `(${plain(surveyLanguages.join(', ').toUpperCase())})` : 'verbatims'
    }`
    const translations = [{ key: '*', text: originalText, label: originalLabel, icon: 'translate' }]

    if (
      ('en' == researcherLanguage && surveyLanguages.some(item => 'en' != item)) ||
      ('en' != researcherLanguage && (surveyLanguages.some(v => v != researcherLanguage) || 'WRD' != country.id))
    ) {
      translations.push({
        key: 'en',
        text: `<b class="p-text-icon">EN</b> Show verbatims in ${languageNames.of('en')}`,
        label: `<b class="p-text-icon">EN</b> Verbatims in ${languageNames.of('en')}`,
        translationsMissing: Boolean(translationsMissing?.en)
      })
    }

    if ('en' != researcherLanguage && surveyLanguages.some(v => v != researcherLanguage)) {
      translations.push({
        key: researcherLanguage,
        text: `<b class="p-text-icon">${plain(researcherLanguage.toUpperCase())}</b> Show verbatims in ${plain(
          languageNames.of(researcherLanguage)
        )}`,
        label: `<b class="p-text-icon">${plain(researcherLanguage.toUpperCase())}</b> Verbatims in ${plain(
          languageNames.of(researcherLanguage)
        )}`,
        translationsMissing: Boolean(translationsMissing?.[researcherLanguage])
      })
    }

    return translations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country.id, locale, targetingLocales, translationsMissing, features.ai_assistants_oe_translation.disabled])

  const itemTemplate = option =>
    option.hidden ? null : (
      <span
        data-testid={`stat-view-options-translation-${'*' == option.key ? 'original' : option.key}`}
        id={`stat-view-options-translation-${'*' == option.key ? 'original' : option.key}`}
        className={classNames(
          'stat-view-options-translation',
          `stat-view-options-translation-${'*' == option.key ? 'original' : option.key}`,
          '-dtr-stat-view-options-translation',
          `-dtr-stat-view-options-translation-${'*' == option.key ? 'original' : option.key}`,
          `p-lvl-${option.lvl || 0}`,
          { 'p-item-label': option.icon }
        )}
        data-stat-translation={'*' == option.value ? 'original' : option.value}
        onClick={features.ai_assistants_oe_translation.disabled ? null : () => onTranslationChange(option.key)}
      >
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
        {option.translationsMissing ? <i className={`icon-aytmfd-skipper ${style['skipper-icon']}`} /> : null}
      </span>
    )

  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }}></span>
        {option.translationsMissing && root && (
          <SidebarPopup
            className={`${style['component-popup']}`}
            root={root}
            help={
              <div className={style['component-popup-content']}>
                {'skipper' == translationMessageType ? (
                  <span className={style['component-popup-icon']}>
                    <i className="icon-aytmfd-skipper" />
                  </span>
                ) : (
                  <span className={style['component-popup-icon']}>🙌</span>
                )}
                <span dangerouslySetInnerHTML={{ __html: translationMessage }}></span>
              </div>
            }
            width="300px"
            trigger={
              null != translationMessage && 0 < translationMessage.length && showTransationPopup ? 'hover' : 'none'
            }
          >
            <i className={`icon-aytmfd-skipper ${style['skipper-icon']} ${style['skipper-icon-spinned']}`} />
          </SidebarPopup>
        )}
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  useEffect(() => {
    const translationsMissingKeys = Object.keys(translationsMissing || {})

    if (translationsMissingKeys.includes(translation)) {
      // Denominator = verbatims that need translating = total minus the survey's own source-language
      // originals (which are preserved, not translated). Matches backend total_verbatim_to_translate.
      const sourceLanguage = locale.split('_', 1)[0].toLowerCase()
      const sourceVerbatims = Object.entries(totalVerbatimsByLocale || {}).reduce(
        (sum, [key, byLocale]) => (key.split('_', 1)[0].toLowerCase() == sourceLanguage ? sum + byLocale.total : sum),
        0
      )
      const total = totalVerbatims - sourceVerbatims
      const missing = translationsMissing[translation]
      setTranslationMessage(
        0 < total
          ? `<b>Work in progress!</b><br />Translating ${plain(
              total - missing
            )}/${plain(total)} verbatims to <b>${plain(translation.toUpperCase())}</b><br />All originals are preserved`
          : `<b>Work in progress!</b><br />Translating ${plain(missing)} ${pluralize(
              missing,
              'verbatim',
              'verbatims'
            )} to <b>${plain(translation.toUpperCase())}</b><br />All originals are preserved`
      )
      setTranslationMessageType('skipper')
    } else {
      setTranslationMessage(null)
      setTranslationMessageType(null)
    }
  }, [locale, translation, totalVerbatims, totalVerbatimsByLocale, translationsMissing])

  return 1 < translationsOptions.length ? (
    <div className={style.section}>
      {features.ai_assistants_oe_translation.available ? (
        <>
          <div className={style['section-title']}>Verbatim&nbsp;translations</div>
          <div className={`${style.row} ${style['row-skipper']}`}>
            <div className="p-col">
              <a
                className="p-component p-button p-button-rounded p-button-blue"
                href={features.ai_assistants_oe_translation.onPackage.upgradeURL}
                rel="noreferrer"
              >
                <i className="icon-aytmfd-skipper" />
                {features.ai_assistants_oe_translation.onPackage.text.replace(' for ', ' ')}
              </a>
            </div>
            {root && (
              <SidebarPopup
                id="unlock-translation"
                root={root}
                help={
                  <span>
                    <b>{features.ai_assistants_oe_translation.onPackage.text}</b>
                    <br />
                    Skipper will translate all verbatims to{' '}
                    {translationsOptions.slice(1).map((option, index) => (
                      <Fragment key={option.key}>
                        {0 < index ? ', ' : null}
                        <b>{option.key.toUpperCase()}</b>
                      </Fragment>
                    ))}
                    .<br />
                    All original responses will be preserved.
                  </span>
                }
                width="300px"
              />
            )}
          </div>
        </>
      ) : features.ai_assistants_oe_translation.disabled ? null : (
        <>
          <div className={style['section-title']}>Verbatim&nbsp;translations</div>
          <div className={`${style.row} p-fluid`}>
            <div className="p-field p-col-12">
              <Dropdown
                id="stat-view-options-translation-selector"
                data-testid="stat-view-options-translation-selector"
                className={classNames(
                  'p-dropdown-rounded p-dropdown-left p-dropdown-no-scroll',
                  'stat-view-options-translation-selector',
                  '-dtr-stat-view-options-translation-selector'
                )}
                placeholder="Select..."
                value={translation || '*'}
                options={translationsOptions}
                optionValue="key"
                optionLabel="text"
                optionDisabled="disabled"
                itemTemplate={itemTemplate}
                valueTemplate={valueTemplate}
                fixedDropdownDir="bottom"
                disabled={features.ai_assistants_oe_translation.disabled}
                onChange={
                  features.ai_assistants_oe_translation.disabled ? null : event => onTranslationChange(event.value)
                }
                onShow={features.ai_assistants_oe_translation.disabled ? null : () => setShowTransationPopup(false)}
                onHide={features.ai_assistants_oe_translation.disabled ? null : () => setShowTransationPopup(true)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  ) : null
}

Translations.propTypes = {
  root: PropTypes.any,
  model: PropTypes.object,
  data: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  onTranslationChange: PropTypes.func
}

Translations.displayName = 'Translations'

export default Translations
